/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/unbound-method */
import { type QueryClient } from '@tanstack/react-query';
import { type Middleware } from 'redux';
import {
  ADD_TO_CART,
  type ActionAddToCart,
  type ActionClearCheckout,
  type ActionClearEditOrder,
  type ActionInitExistingOrder,
  type ActionRemoveFromCart,
  type ActionSelectPaymentType,
  type ActionUpdateCart,
  type ActionUpdateEditOrder,
  CLEAR_CHECKOUT,
  CLEAR_EDIT_ORDER,
  INIT_EXISTING_ORDER,
  REMOVE_FROM_CART,
  SELECT_PAYMENT_TYPE,
  UPDATE_CART,
  UPDATE_EDIT_ORDER,
  clearCheckout,
  clearDeliveryWindows,
  fetchWindows,
  turnOnSplitPayment,
  updateOrder,
} from '~shared/actions/checkout';
// eslint-disable-next-line import/no-deprecated
import { fetchDeliveries } from '~shared/actions/deliveries';
import {
  type FetchNextAvailableDatesOptions,
  // eslint-disable-next-line import/no-deprecated
  fetchNextAvailableDates,
} from '~shared/actions/nextAvailableDates';
// eslint-disable-next-line import/no-deprecated
import { autobillPrescription, fetchPrescriptions } from '~shared/actions/prescriptions';
import { getItemsInOrder, getPrescriptionsInOrder } from '~shared/features/checkout/selectors/getItems';
import { getOrder, getOrderAddressId } from '~shared/features/checkout/selectors/getOrder';
import { type Item } from '~shared/features/checkout/types';
import { deleteAddOnPrescription } from '~shared/features/essentials/actions';
import { queries } from '~shared/queries/query-keys';
import { type Prescription, type ReduxDispatch, type ReduxStateShared } from '~shared/types';

const allowedTypes = {
  [ADD_TO_CART]: true,
  [REMOVE_FROM_CART]: true,
  [UPDATE_EDIT_ORDER]: true,
  [UPDATE_CART]: true,
  [SELECT_PAYMENT_TYPE]: true,
  [INIT_EXISTING_ORDER]: true,
  [CLEAR_CHECKOUT]: true,
  [CLEAR_EDIT_ORDER]: true,
};

const ORDER_PRICING_UPDATE_CART_KEYS = {
  payment_method_id: true,
  second_payment_method_id: true,
  shipment_payment_methods: true,
  include_sharps_container: true,
  items: true,
  selected_payment_types: true,
  tip_amount: true,
  tip_payment_method_id: true,
  date: true,
  pay_with_apple_pay: true,
};

type Actions =
  | ActionAddToCart
  | ActionClearCheckout
  | ActionClearEditOrder
  | ActionInitExistingOrder
  | ActionUpdateCart
  | ActionUpdateEditOrder
  | ActionSelectPaymentType
  | ActionRemoveFromCart;

const cart =
  (queryClient: QueryClient): Middleware<unknown, ReduxStateShared, ReduxDispatch> =>
  ({ dispatch, getState }) =>
  (next) =>
  (action: Actions) => {
    const result = next(action);

    if (!allowedTypes[action.type]) {
      return result;
    }

    const state = getState();
    const prescriptions = getPrescriptionsInOrder(state);

    // Delivery windows are only for courier. Mail uses specific names (e.g. fedex_priority_overnight), not "mail".
    const isOrderCourierOnly = (deliveryMethod: string | undefined) => deliveryMethod === 'courier';

    switch (action.type) {
      case INIT_EXISTING_ORDER: {
        const order = action?.payload?.order;
        if ((order?.date || order?.address_id) && isOrderCourierOnly(order?.delivery_method)) {
          dispatch(fetchWindows(prescriptions?.map((rx) => rx.id)));
        }

        if (action?.payload?.order?.shipment_payment_methods?.length === 2) {
          dispatch(turnOnSplitPayment());
        }

        queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        break;
      }
      case UPDATE_EDIT_ORDER:
      case UPDATE_CART: {
        const items = getItemsInOrder(state);
        if (!items.length) break;

        if (
          (action.payload.date || action.payload.address_id) &&
          isOrderCourierOnly(getOrder(state)?.delivery_method)
        ) {
          dispatch(fetchWindows(prescriptions?.map((rx) => rx.id)));
        }

        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type... (Delete me to see the full error)
        if (Object.keys(action.payload).some((key) => ORDER_PRICING_UPDATE_CART_KEYS[key])) {
          queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        }

        break;
      }

      case ADD_TO_CART: {
        const { payload } = action;
        const { billing_overrides, resetOrderDate, skipNextAvailableDatesFetch, ...itemKey } = payload;

        const items = getItemsInOrder(state);
        const item = items.find((item: Item) => item.equals(itemKey));
        const prescription = item?.prescription;
        const prescriptions = getPrescriptionsInOrder(state);
        const addressID = getOrderAddressId(state);

        if (prescription) {
          // Cross-sell items are derived from unbundled products.
          // Only fetch dates for relevant prescriptions when we do not need to show cross-sell products
          const prescriptionIdsInOrder = prescriptions.map((rx: Prescription) => rx.id);
          const nextAvailableDateOptions: FetchNextAvailableDatesOptions = {
            unbundledPrescriptionIDs: [prescription.id],
            bundledPrescriptionIDs: prescriptionIdsInOrder.length > 1 ? prescriptionIdsInOrder : [],
            addressID,
          };

          // eslint-disable-next-line import/no-deprecated, promise/catch-or-return
          dispatch(autobillPrescription(prescription.id, billing_overrides)).then(() => {
            queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
            // The correct order is to refresh deliveries after autobill, before refreshing prescriptions.
            // Why? Because refreshing prescriptions includes logic to remove any prescriptions from cart that aren't
            // ready to schedule. (see useRemoveUnschedulablePrescriptionsFromCart).
            // A delivery will appear not ready to schedule if it's refreshed during autobill and not after.
            // eslint-disable-next-line import/no-deprecated, promise/catch-or-return, promise/no-nesting, promise/always-return
            dispatch(fetchDeliveries()).then(() => {
              // eslint-disable-next-line import/no-deprecated
              dispatch(fetchPrescriptions());
            });
            if (!skipNextAvailableDatesFetch) {
              return (
                // eslint-disable-next-line import/no-deprecated, promise/no-nesting
                dispatch(fetchNextAvailableDates(nextAvailableDateOptions)).then(() => {
                  if (resetOrderDate) {
                    // resetOrderDate is passed in for flows that are likely to update the next available date to an
                    // earlier date. This is currently used for 'Get it sooner' flows, where patients can choose
                    // different payment options as a way to get their medication sooner.
                    // In these scenarios, we should reset the order date and have select date component
                    // calculate the order date based on the new next available date
                    dispatch(updateOrder({ date: undefined }));
                  }
                  return undefined;
                })
              );
            }
            if (resetOrderDate) {
              dispatch(updateOrder({ date: undefined }));
            }
            return undefined;
          });
        }

        dispatch(fetchWindows(prescriptions.map((rx) => rx.id)));

        break;
      }

      case SELECT_PAYMENT_TYPE: {
        queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        break;
      }

      case REMOVE_FROM_CART: {
        const { payload } = action;
        const { prescription, resource_type } = payload;

        if (resource_type === 'Prescription' && prescription?.is_add_on_otc) {
          try {
            deleteAddOnPrescription(prescription?.id);
          } catch {
            // no op:
            // Deleting the Add On prescription is for garbage collecting purposes on the backend. The client doesn't care about
            // the result of this endpoint, nor does any part of the UI rely on its loading/success/error.
          }
        }

        const items = getItemsInOrder(state);

        if (items.length === 0) {
          dispatch(clearCheckout());
          break;
        }

        queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
        const prescription_ids: number[] = items.map((item: Item) => item.prescription.id);
        dispatch(fetchWindows(prescription_ids));

        break;
      }

      case CLEAR_EDIT_ORDER:
      case CLEAR_CHECKOUT: {
        dispatch(clearDeliveryWindows());
        break;
      }

      default:
    }

    return result;
  };

export default cart;
/* eslint-enable sonarjs/cognitive-complexity */
/* eslint-enable @typescript-eslint/unbound-method */
